import axios from 'axios';
import {capitalizeFilter} from "@/utils/formatting";
import * as Sentry from "@sentry/browser";

const instance = axios.create({
  baseURL: process.env.VUE_APP_SERVERURL,
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFTOKEN',
});

/**
 * Generate an api endpoint to interact with Django Rest Framework
 * Automatically generate method get with pk, get list and get options with post definition
 */
export class ApiGenerator {
  /**
   *
   * @param instance instance of axios to make http calls
   * @param url url of endpoint
   * @param name name of method to use in javascript
   * @param plural plural name of method if not follow the rule of add 's' at the end
   */
  constructor(instance, url, name, plural) {
    this.instance = instance;
    this.url = url;
    this.name = name;
    this.plural = plural;
  }

  generateEndpoint() {
    const capitalizedMethod = capitalizeFilter(this.name);
    const capitalizedPlural = this.plural ? capitalizeFilter(this.plural) : capitalizedMethod + 's';
    const apiGet = 'get' + capitalizedMethod;
    const apiGetList = 'get' + capitalizedPlural;
    return {
      [apiGet]: (pk) => this.generateElementApi(pk),
      [apiGetList]: (params) => this.generateListApi(params),
      [apiGet + 'Options']: () => this.instance.options(this.url),
      ['create' + capitalizedMethod]: (data) => this.instance.post(this.url, data),
      ['update' + capitalizedMethod]: (pk, data) =>
        this.getElementUrl(pk).then(url => this.instance.patch(url, data)),
      ['delete' + capitalizedMethod]: (pk) =>
        this.getElementUrl(pk).then(url => this.instance.delete(url)),
    };
  }

  generateElementApi(pk) {
    return this.getElementUrl(pk)
      .then(url => this.instance.get(url));
  }

  getElementUrl(pk) {
    if (typeof pk !== 'number') {
      Sentry.captureMessage('getElementUrl: Primary key '.concat(pk, ' is not a number in ', this.url, pk));
      return Promise.reject(null);
    } else {
      return Promise.resolve(this.url.concat(pk.toString(), '/'));
    }
  }

  generateListApi(params) {
    return this.instance.get(this.url, {params: params});
  }
}

export default {
  getCurrentUser: () => {
    return instance.get('users/current-user/');
  },
  changePassword: (old_password, new_password) => {
    return instance.post('users/change-password/', {
      "old_password": old_password,
      "new_password": new_password
    });
  },
  getHome: () => {
    return instance.get('home/').then(response => response.data);
  },
  updateAdjuvantTreatmentInSurgery: (surgeryId, data) => {
    return instance.post('surgeries/' + surgeryId.toString() + '/update-adjuvant-treatments/', data);
  },
  ...new ApiGenerator(instance, 'users/', 'user').generateEndpoint(),
  ...new ApiGenerator(instance, 'patients/', 'patient').generateEndpoint(),
  ...new ApiGenerator(instance, 'centers/', 'center').generateEndpoint(),
  ...new ApiGenerator(instance, 'surgery-description/', 'surgeryDescription').generateEndpoint(),
  ...new ApiGenerator(instance, 'surgeries/', 'surgery', 'surgeries').generateEndpoint(),
  ...new ApiGenerator(instance, 'novoeight-treatment/', 'novoeightTreatment').generateEndpoint(),
  ...new ApiGenerator(instance, 'transfusion/', 'transfusion').generateEndpoint(),
  ...new ApiGenerator(instance, 'blood-test/', 'bloodTest').generateEndpoint(),
  ...new ApiGenerator(instance, 'adjuvant-treatment/', 'adjuvantTreatment').generateEndpoint(),
  ...new ApiGenerator(instance, 'previous-surgeries/', 'previousSurgery', 'previousSurgeries').generateEndpoint(),
  ...new ApiGenerator(instance, 'post-surgeries/', 'postSurgery', 'postSurgeries').generateEndpoint(),
  ...new ApiGenerator(instance, 'final-evaluations/', 'finalEvaluation').generateEndpoint(),
};